import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { jobsService } from "../services/jobs";
import { useDashboardFilters, getUserIdFromToken } from "../hooks/useDashboardFilters";
import { useDashboardSort } from "../hooks/useDashboardSort";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Skeleton } from "../components/ui/Skeleton";
import MarkdownModal from "../components/MarkdownModal";
import type { JobListItem, ApplicationStatus } from "../types";

const col = createColumnHelper<JobListItem>();

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return "—";
  const d = new Date(v);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const STATUS_OPTIONS: ApplicationStatus[] = ["saved", "applied", "interview", "offer", "rejected", "expired", "deleted"];

const inputClass =
  "px-3 py-2 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { sorting, setSorting } = useDashboardSort();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [modal, setModal] = useState<{ title: string; content: string } | null>(null);

  const userId = getUserIdFromToken();
  const { filters, setFilters, resetFilters } = useDashboardFilters(userId);
  const { titleFilter, companyFilter, statusFilter, dateField, dateFrom, dateTo } = filters;

  useEffect(() => {
    jobsService.list().then((data) => {
      setJobs(data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (titleFilter && !j.job_title?.toLowerCase().includes(titleFilter.toLowerCase())) return false;
      if (companyFilter && !j.company_name?.toLowerCase().includes(companyFilter.toLowerCase())) return false;
      if (statusFilter === "active") {
        if (j.application_status === "deleted" || j.application_status === "expired" || j.application_status === "rejected") return false;
      } else if (statusFilter) {
        if (j.application_status !== statusFilter) return false;
      }
      if (dateFrom || dateTo) {
        const raw = j[dateField] as string | null;
        if (!raw) return false;
        const val = raw.slice(0, 10);
        const lo = dateFrom && dateTo && dateFrom > dateTo ? dateTo : dateFrom;
        const hi = dateFrom && dateTo && dateFrom > dateTo ? dateFrom : dateTo;
        if (lo && val < lo) return false;
        if (hi && val > hi) return false;
      }
      return true;
    });
  }, [jobs, titleFilter, companyFilter, statusFilter, dateField, dateFrom, dateTo]);

  const columns = useMemo(
    () => [
      col.accessor("company_name", {
        header: "Company",
        cell: (info) => info.getValue() || "—",
      }),
      col.accessor("job_title", {
        header: "Title",
        cell: (info) => (
          <Link
            to={`/jobs/${info.row.original.id}`}
            className="text-accent font-medium no-underline hover:underline"
          >
            {info.getValue() || "Unnamed Role"}
          </Link>
        ),
      }),
      col.accessor("alignment_score", {
        header: "Alignment",
        cell: (info) => {
          const v = info.getValue();
          return v !== null ? `${v}%` : "—";
        },
      }),
      col.accessor("success_prob", {
        header: "Success %",
        cell: (info) => {
          const v = info.getValue();
          return v !== null ? `${v}%` : "—";
        },
      }),
      col.accessor("posting_date", {
        header: "Posted",
        cell: (info) => fmtDate(info.getValue()),
      }),
      col.accessor("deadline_date", {
        header: "Deadline",
        cell: (info) => {
          const v = info.getValue();
          if (!v) return "—";
          const past = new Date(v) < new Date();
          return <span className={past ? "text-error font-medium" : ""}>{fmtDate(v)}</span>;
        },
      }),
      col.accessor("applied_date", {
        header: "Applied",
        cell: (info) => fmtDate(info.getValue()),
      }),
      col.accessor("created_at", {
        header: "Added",
        cell: (info) => fmtDate(info.getValue()),
      }),
      col.accessor("application_status", {
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          return (
            <Badge variant={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          );
        },
      }),
      col.accessor("source_url", {
        header: "Source",
        enableSorting: false,
        cell: (info) => {
          const url = info.getValue();
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open job posting"
              className="text-slate-400 no-underline hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200"
            >
              ↗
            </a>
          ) : (
            <span className="text-slate-300 dark:text-slate-600">—</span>
          );
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="max-w-[1200px] mx-auto px-6 pt-6 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 m-0">
          Job Applications
        </h1>
        <Button variant="primary" onClick={() => navigate("/jobs/new")}>
          + Add Job
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <input
          type="text"
          placeholder="Filter by company..."
          value={companyFilter}
          onChange={(e) => setFilters({ companyFilter: e.target.value })}
          className={`${inputClass} min-w-[180px]`}
        />
        <input
          type="text"
          placeholder="Filter by title..."
          value={titleFilter}
          onChange={(e) => setFilters({ titleFilter: e.target.value })}
          className={`${inputClass} min-w-[180px]`}
        />
        <select
          value={statusFilter}
          onChange={(e) => setFilters({ statusFilter: e.target.value as ApplicationStatus | "" | "active" })}
          aria-label="Filter by status"
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={dateField}
          onChange={(e) => setFilters({ dateField: e.target.value as "created_at" | "applied_date" | "deadline_date" | "posting_date" })}
          aria-label="Filter by date field"
          className={`${inputClass} cursor-pointer`}
        >
          <option value="created_at">Added</option>
          <option value="applied_date">Applied</option>
          <option value="deadline_date">Deadline</option>
          <option value="posting_date">Posted</option>
        </select>
        <input
          type="date"
          aria-label="Date from"
          value={dateFrom}
          onChange={(e) => setFilters({ dateFrom: e.target.value })}
          onBlur={(e) => setFilters({ dateFrom: e.target.value })}
          className={inputClass}
        />
        <input
          type="date"
          aria-label="Date to"
          value={dateTo}
          onChange={(e) => setFilters({ dateTo: e.target.value })}
          onBlur={(e) => setFilters({ dateTo: e.target.value })}
          className={inputClass}
        />
        {(titleFilter || companyFilter || statusFilter !== "active" || dateFrom || dateTo) && (
          <Button
            variant="secondary"
            size="sm"
            onClick={resetFilters}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3 py-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-4 items-center">
              <Skeleton variant="line" className="flex-1" />
              <Skeleton variant="line" className="w-24" />
              <Skeleton variant="line" className="w-16" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-600 dark:text-slate-400">
          {jobs.length === 0
            ? "No jobs yet. Add your first job posting above."
            : "No jobs match the current filters."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={[
                        "px-3 py-2.5 text-left text-sm font-semibold text-slate-700 dark:text-slate-300",
                        "border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap select-none",
                        "bg-slate-50 dark:bg-slate-800",
                        header.column.getCanSort() ? "cursor-pointer hover:text-slate-900 dark:hover:text-slate-100" : "cursor-default",
                      ].join(" ")}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === "asc"
                        ? " ↑"
                        : header.column.getIsSorted() === "desc"
                        ? " ↓"
                        : header.column.getCanSort()
                        ? " ↕"
                        : ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, i) => (
                <tr
                  key={row.id}
                  className={
                    i % 2 === 0
                      ? "bg-white dark:bg-slate-900"
                      : "bg-slate-50 dark:bg-slate-800/50"
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2.5 border-b border-slate-200 dark:border-slate-700 align-middle text-slate-700 dark:text-slate-300"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Markdown modal */}
      {modal && (
        <MarkdownModal title={modal.title} content={modal.content} onClose={() => setModal(null)} />
      )}
    </div>
  );
}
